import { withoutScenarioTeams } from './scenarioTeams';
/**
 * branchTree.js — the V3 scenario model.
 *
 * A V3 scenario is a TREE of branches. Every branch owns a complete,
 * independent timeline (exactly the V2 shape: `phases` + `keyframes` +
 * `timelineDuration`), so the existing timeline editor works on a branch
 * unchanged. At the end of a branch's timeline the story splits: each child
 * branch is one possible continuation.
 *
 *   scenario
 *     └─ tree (root branch)          ← every scenario starts here
 *          ├─ branch  (order 1)      ← default continuation
 *          │    └─ branch …
 *          └─ branch  (order 2)
 *
 * Sibling order is the branch's `order` (1-based, unique per sibling group):
 * order 1 is the default choice when arriving from the parent, which is also
 * what the master play-through follows.
 *
 * Invariants:
 *   • Every branch has a unique `id`.
 *   • `order` values within a sibling group are unique and normalised to
 *     1..N whenever the tree is mutated.
 *   • A branch's timeline never contains keys before `startLockTime` (0 for
 *     children, null for the root) — that first key is inherited from the
 *     parent branch's end state and is read-only in the editor.
 */
import { createEmptyPhase, createEmptyPitchState, migratePitchState } from './data';
import { sampleEffectivePosition } from './sampling';

/** Scene version written by the V3 frontend. */
export const V3_SCENE_VERSION = '3.0';

/** Time of the inherited, read-only first key on every non-root branch. */
export const INHERITED_KEY_TIME = 0;

/** Per-branch default timeline length (seconds) — matches V2. */
export const DEFAULT_BRANCH_DURATION = 15;

let _idSeed = 0;
/** Unique branch id ('branch-<time>-<n>'). */
export function newBranchId() {
    _idSeed += 1;
    return `branch-${Date.now().toString(36)}-${_idSeed}`;
}

const clone = (v) => JSON.parse(JSON.stringify(v));

const ITEM_COLLECTIONS = ['balls', 'cones', 'targets', 'zones'];

/** Item membership belongs to the scenario; positions and keys belong to branches. */
export function reconcileTreeItems(tree, removed = {}) {
    if (!tree) return tree;
    const catalog = Object.fromEntries(ITEM_COLLECTIONS.map(kind => [kind, new Map()]));
    for (const { branch } of flattenTree(tree)) {
        for (const kind of ITEM_COLLECTIONS) {
            for (const item of branch.phases?.[0]?.start?.[kind] || []) {
                if (!(removed[kind] || []).includes(item.id) && !catalog[kind].has(item.id)) {
                    catalog[kind].set(item.id, item);
                }
            }
        }
    }
    const visit = branch => {
        const phases = (branch.phases || []).map(phase => {
            const start = { ...phase.start };
            for (const kind of ITEM_COLLECTIONS) {
                const own = (start[kind] || []).filter(item => !(removed[kind] || []).includes(item.id));
                start[kind] = [...own, ...[...catalog[kind].values()]
                    .filter(item => !own.some(existing => existing.id === item.id)).map(clone)];
            }
            return { ...phase, start };
        });
        const keyframes = (branch.keyframes || []).filter(track =>
            !(removed[`${track.type}s`] || []).includes(track[`${track.type}Id`]));
        return { ...branch, phases, keyframes, children: (branch.children || []).map(visit) };
    };
    const next = visit(tree);
    return JSON.stringify(next) === JSON.stringify(tree) ? tree : next;
}

/** Apply explicit deletions before collecting items from the other branches. */
export function reconcileWorkingItems(tree, previousStart, workingStart) {
    const removed = Object.fromEntries(ITEM_COLLECTIONS.map(kind => [kind,
        (previousStart?.[kind] || []).filter(item =>
            !(workingStart?.[kind] || []).some(current => current.id === item.id)).map(item => item.id)]));
    return reconcileTreeItems(tree, removed);
}

/**
 * Create a branch — a self-contained timeline plus its children.
 * @param {object} [opts]
 * @param {string} [opts.id]
 * @param {string} [opts.name]
 * @param {number} [opts.order=1]
 * @param {object} [opts.startState] - pitch state for the branch's first phase
 */
export function createBranch({ id, name = 'Branch', order = 1, startState = null } = {}) {
    const phase = createEmptyPhase(1);
    if (startState) phase.start = clone(startState);
    return {
        id: id || newBranchId(),
        name,
        order,
        phases: [phase],
        keyframes: [],
        timelineDuration: DEFAULT_BRANCH_DURATION,
        children: [],
    };
}

/**
 * Create a brand-new empty V3 scenario (a lone root branch).
 */
export function createEmptyScenarioV3(name = 'New Scenario') {
    return {
        schemaVersion: V3_SCENE_VERSION,
        sceneVersion: V3_SCENE_VERSION,
        name,
        tree: createBranch({ id: 'root', name: 'Root', order: 1 }),


    };
}

// ─────────────────────────────────────────────────────────────
// TRAVERSAL
// ─────────────────────────────────────────────────────────────

/** Children of a branch, sorted by priority (order). */
export function sortedChildren(branch) {
    return [...((branch && branch.children) || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
}

/** Find a branch by id anywhere in the tree (null when missing). */
export function findBranch(tree, branchId) {
    if (!tree) return null;
    if (tree.id === branchId) return tree;
    for (const child of tree.children || []) {
        const hit = findBranch(child, branchId);
        if (hit) return hit;
    }
    return null;
}

/** Path from the root down to `branchId` inclusive ([root, …, branch]); [] if unknown. */
export function findBranchPath(tree, branchId) {
    if (!tree) return [];
    if (tree.id === branchId) return [tree];
    for (const child of tree.children || []) {
        const sub = findBranchPath(child, branchId);
        if (sub.length) return [tree, ...sub];
    }
    return [];
}

/** The parent of `branchId` (null for the root or an unknown id). */
export function findParent(tree, branchId) {
    const path = findBranchPath(tree, branchId);
    return path.length > 1 ? path[path.length - 2] : null;
}

/** Ancestors of `branchId`, root first ([...] without the branch itself). */
export function getAncestors(tree, branchId) {
    const path = findBranchPath(tree, branchId);
    return path.slice(0, -1);
}

/** Is this the root branch? */
export function isRootBranch(tree, branchId) {
    return !!tree && tree.id === branchId;
}

/**
 * Split a MASTER TIMELINE position into the branch it lands on and how far into
 * that branch's own timeline it is.
 *
 * The master timeline is deliberately NOT measured in seconds. It is a bar made
 * of one equal segment per branch along the master path (root → highest-priority
 * child → …), so a position is a real number in [0, chain.length]:
 *
 *   pos = 2.25  →  third branch on the path, 25% of the way through its timeline
 *
 * Because each branch keeps its own length, the fraction is resolved against
 * that branch's timeline when it is converted to a playhead (see masterTimeFor).
 *
 * @param {object[]} chain  branches from topPriorityChain()
 * @param {number} pos      master position, 0 .. chain.length
 * @returns {{ index: number, frac: number, branch: object|null }}
 */
export function resolveMasterPos(chain, pos) {
    if (!chain || chain.length === 0) return { index: 0, frac: 0, branch: null };
    const clamped = Math.min(chain.length, Math.max(0, Number(pos) || 0));
    const index = Math.min(chain.length - 1, Math.floor(clamped));
    const frac = Math.min(1, Math.max(0, clamped - index));
    return { index, frac, branch: chain[index] };
}

/**
 * The inverse of resolveMasterPos: where the master handle sits for a branch
 * and a time inside it. `time / duration` normalises the branch's own timeline,
 * so branches of different lengths all occupy one equal segment.
 *
 * @param {object[]} chain
 * @param {string} branchId
 * @param {number} time      seconds into the branch
 * @param {number} duration  that branch's timeline length in seconds
 */
export function masterPosFor(chain, branchId, time, duration) {
    if (!chain || chain.length === 0) return 0;
    const i = chain.findIndex(b => b.id === branchId);
    if (i < 0) return 0;
    const frac = duration > 0 ? Math.min(1, Math.max(0, (Number(time) || 0) / duration)) : 0;
    return i + frac;
}

/**
 * Seconds into a branch for a normalised fraction of its timeline. This is the
 * "normalised by the timeline's length" step: the same fraction of the master
 * bar means a different number of seconds in a 15 s branch than in a 30 s one.
 */
export function masterTimeFor(duration, frac) {
    const d = Number(duration) || 0;
    const f = Math.min(1, Math.max(0, Number(frac) || 0));
    return d * f;
}

/**
 * A playhead's position as a FRACTION of its timeline, clamped to [0, 1].
 *
 * This is what carries a playhead across a branch switch. Half way through a
 * 50 s branch is still half way in whatever branch you move to, however long
 * that one is — so switching hands the position over instead of snapping the
 * playhead back to 00:00 (and the master bar's handle, which is derived from the
 * playhead, back to the start of the new segment).
 *
 * Pairs with masterTimeFor, which is the other direction.
 */
export function playheadFraction(time, duration) {
    const d = Number(duration) || 0;
    if (d <= 0) return 0;
    return Math.min(1, Math.max(0, (Number(time) || 0) / d));
}

/**
 * Siblings of `branchId` including itself, sorted by priority.
 * The root's group is itself → [root] (it always fills the middle column).
 */
export function getSiblings(tree, branchId) {
    const branch = findBranch(tree, branchId);
    if (!branch) return [];
    const parent = findParent(tree, branchId);
    // A branch with no parent is the root — its priority group is itself, which
    // is why the root always occupies the middle column of the tree view.
    if (!parent) return [branch];
    return sortedChildren(parent);
}

/**
 * The walk a master play-through takes: the root, then the top-priority child
 * at every step, down to the leaf.
 */
export function topPriorityChain(tree) {
    const chain = [];
    let cur = tree;
    while (cur) {
        chain.push(cur);
        cur = sortedChildren(cur)[0] || null;
    }
    return chain;
}

/** The branch a master play-through visits after `branchId` (its top child). */
export function nextBranchInChain(branch) {
    return sortedChildren(branch)[0] || null;
}

/**
 * The branch the user has chosen under `parent`, or the DEFAULT: its
 * highest-priority (order 1) child.
 *
 * Choices are a per-session, per-sibling-set record — { parentBranchId:
 * chosenBranchId } — and are never saved. Priority order is only the initial
 * value for a set the user has not touched, which is why a fresh load plays the
 * default path and why navigating around cannot "reset" a set back to it.
 */
export function chosenChild(parent, choices = {}) {
    const kids = sortedChildren(parent);
    if (!kids.length) return null;
    const picked = choices ? choices[parent.id] : null;
    return kids.find(c => c.id === picked) || kids[0];
}

/**
 * The path the user's choices describe, from `branch` down to a leaf: at every
 * split take the chosen branch, falling back to the default where nothing has
 * been chosen. This — not a re-derivation from priority — is what a master
 * play-through follows and what the master bar shows.
 */
export function chosenPath(branch, choices = {}) {
    const out = [];
    const seen = new Set();
    let cur = branch;
    while (cur && !seen.has(cur.id)) {   // seen: guard against corrupted data
        seen.add(cur.id);
        out.push(cur);
        cur = chosenChild(cur, choices);
    }
    return out;
}

/** Resolve Master Play from the visible position; a completed path replays from root. */
export function masterPlaybackStart(tree, openId, playhead, choices = {}) {
    const current = findBranch(tree, openId) || tree;
    const remaining = chosenPath(current, choices);
    const duration = current?.timelineDuration ?? DEFAULT_BRANCH_DURATION;
    const finished = remaining.length === 1 && playhead >= duration - 0.01;
    const chain = finished ? chosenPath(tree, choices) : remaining;
    return { chain, time: finished ? 0 : Math.max(0, Math.min(playhead, duration)) };
}

// ─────────────────────────────────────────────────────────────
// MUTATION — every function returns a NEW tree (immutable)
// ─────────────────────────────────────────────────────────────

/** Normalise sibling order to 1..N (mutates the given array copy in place). */
function normaliseOrder(children) {
    const sorted = [...children].sort((a, b) => (a.order || 0) - (b.order || 0));
    sorted.forEach((c, i) => { c.order = i + 1; });
    return sorted;
}

/**
 * Replace one branch anywhere in the tree using an updater.
 * Returns a new tree; unknown ids return the tree unchanged.
 */
export function updateBranch(tree, branchId, updater) {
    if (!tree) return tree;
    if (tree.id === branchId) return updater(tree);
    let changed = false;
    const children = (tree.children || []).map((child) => {
        const next = updateBranch(child, branchId, updater);
        if (next !== child) changed = true;
        return next;
    });
    return changed ? { ...tree, children } : tree;
}

/**
 * Add a new sibling of `branchId`, named `name`, at position `order`
 * (clamped). Returns { tree, branch } — the new branch is also returned so the
 * caller can select/enter it.
 */
export function addSibling(tree, branchId, name, order = null) {
    const parent = findParent(tree, branchId) || null;
    if (!parent) return { tree, branch: null };   // the root has no siblings
    return addChild(tree, parent.id, name, order);
}

/**
 * Add a new child branch to `parentId`. The new branch inherits the parent's
 * end state (see seedBranchFromParent). Returns { tree, branch }.
 */
export function addChild(tree, parentId, name = null, order = null) {
    const parent = findBranch(tree, parentId);
    if (!parent) return { tree, branch: null };
    const siblings = sortedChildren(parent);
    const branch = createBranch({
        name: name || `Branch ${siblings.length + 1}`,
        order: siblings.length + 1,
        startState: branchEndState(parent),
    });
    seedBranchFromParent(parent, branch);
    const children = [...(parent.children || []), branch];
    const at = order === null ? children.length : order;
    // place the new branch at the requested position
    const ordered = normaliseOrder(children);
    const moved = ordered.filter(c => c.id !== branch.id);
    const idx = Math.max(0, Math.min(moved.length, Math.round(at) - 1));
    moved.splice(idx, 0, branch);
    moved.forEach((c, i) => { c.order = i + 1; });
    const newTree = updateBranch(tree, parentId, (p) => ({ ...p, children: moved }));
    return { tree: newTree, branch };
}

/** Remove a branch (and its sub-tree). Returns the new tree. */
export function removeBranch(tree, branchId) {
    if (!tree || tree.id === branchId) return tree; // never remove the root
    const children = (tree.children || [])
        .filter(c => c.id !== branchId)
        .map(c => removeBranch(c, branchId));
    return { ...tree, children: normaliseOrder(children) };
}

/**
 * Set a branch's priority number. Siblings are renumbered 1..N around it, so
 * the value stays unique and contiguous (typing "1" makes it the default).
 */
export function setBranchOrder(tree, branchId, order) {
    const parent = findParent(tree, branchId);
    if (!parent) return tree;
    const siblings = sortedChildren(parent);
    const target = Math.max(1, Math.min(siblings.length, Math.round(Number(order) || 1)));
    const current = siblings.findIndex(s => s.id === branchId);
    if (current === -1) return tree;
    const rest = siblings.filter(s => s.id !== branchId);
    const moving = siblings[current];
    rest.splice(target - 1, 0, moving);
    rest.forEach((s, i) => { s.order = i + 1; });
    return updateBranch(tree, parent.id, (p) => ({ ...p, children: rest }));
}

/**
 * Move a branch one step up/down in its sibling priority list.
 * `dir` is 'up' | 'down'.
 */
export function moveBranchPriority(tree, branchId, dir) {
    const parent = findParent(tree, branchId);
    if (!parent) return tree;
    const siblings = sortedChildren(parent);
    const i = siblings.findIndex(s => s.id === branchId);
    if (i === -1) return tree;
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= siblings.length) return tree;   // already at the end
    const next = [...siblings];
    [next[i], next[j]] = [next[j], next[i]];
    next.forEach((s, k) => { s.order = k + 1; });
    return updateBranch(tree, parent.id, (p) => ({ ...p, children: next }));
}

/** Rename a branch. */
export function renameBranch(tree, branchId, name) {
    return updateBranch(tree, branchId, (b) => ({ ...b, name: String(name || '').trim() || b.name }));
}

// ─────────────────────────────────────────────────────────────
// INHERITANCE — a branch starts where its parent ended
// ─────────────────────────────────────────────────────────────

/** Deepest timeline sample time for a branch (its playable length). */
export function branchEndTime(branch) {
    return branch?.timelineDuration ?? DEFAULT_BRANCH_DURATION;
}

const lastKeyValue = (track, kind) => {
    const keys = (track && track[kind]) || [];
    if (!keys.length) return null;
    return [...keys].sort((a, b) => a.time - b.time)[keys.length - 1];
};

/**
 * Where an ATTACHABLE track (ball / zone) actually ENDS: its effective position
 * at the end of the branch. For something that was being carried that is the
 * carrier's position — not the last position key the user placed, which the item
 * has not occupied since before the attach.
 *
 * Returns null for a track with no attachment keys, whose last key IS its end.
 */
function attachedEndPosition(track, tracks, endTime) {
    if (!track || (track.attach || []).length === 0) return null;
    const pos = sampleEffectivePosition(track, endTime, tracks);
    return Array.isArray(pos) ? pos : null;
}

/**
 * The pitch state at the END of a branch's timeline: its first phase's start
 * state with every track's final key values applied. This is what the next
 * branch's timeline begins from.
 */
export function branchEndState(branch) {
    if (!branch) return createEmptyPitchState();
    const state = clone((branch.phases && branch.phases[0] && branch.phases[0].start) || createEmptyPitchState());

    const tracks = branch.keyframes || [];
    const endTime = branchEndTime(branch);
    for (const track of tracks) {
        const applyPosition = (arr, idField, idValue) => {
            const k = lastKeyValue(track, 'position');
            if (!k || !Array.isArray(k.position)) return;
            const item = (arr || []).find(o => o[idField] === idValue);
            if (item) item.position = clone(k.position);
        };
        if (track.type === 'player') {
            const side = track.side === 'away' ? 'away' : 'home';
            const players = (state[side] && state[side].players) || [];
            const p = players.find(pl => pl.id === `${track.side}-${track.index}`) || players[track.index];
            if (p) {
                // A player who was FOLLOWING another player does not end at
                // their own last key — they end wherever the follow left them.
                // Inheriting the key instead would open the child branch with
                // the shadow back at a spot it had not stood in since before
                // this branch ran (the same bug a carried ball used to have).
                const carriedEnd = attachedEndPosition(track, tracks, endTime);
                const k = lastKeyValue(track, 'position');
                if (carriedEnd && Array.isArray(carriedEnd)) p.position = clone(carriedEnd);
                else if (k && Array.isArray(k.position)) p.position = clone(k.position);
            }
        } else if (track.type === 'ball') {
            // A ball that was being CARRIED does not end where its own last
            // position key is — it ends where its carrier is. Inheriting the
            // key's value is what started the next branch with the ball back at
            // a spot it had not occupied since before this branch ran.
            const carriedEnd = attachedEndPosition(track, tracks, endTime);
            const ball = (state.balls || []).find(b => b.id === track.ballId);
            if (carriedEnd && ball) ball.position = clone(carriedEnd);
            else applyPosition(state.balls, 'id', track.ballId);
            const a = lastKeyValue(track, 'attach');
            if (ball && a) ball.attached = a.value === 'none' ? null : a.value;
        } else if (track.type === 'cone') {
            applyPosition(state.cones, 'id', track.coneId);
        } else if (track.type === 'target') {
            applyPosition(state.targets, 'id', track.targetId);
        } else if (track.type === 'zone') {
            const zone = (state.zones || []).find(z => z.id === track.zoneId);
            // A zone that was DRAGGED, or that was FOLLOWING a player, does not
            // end on the spot it was drawn on. Its CENTRE is what the shape is
            // anchored to and what a drag measures its delta from, so a start
            // state still holding the creation spot makes the first drag in the
            // child move the shape by the distance it had already been carried —
            // a jump, not a move. Same rule as the carried ball and the shadow.
            const carriedEnd = attachedEndPosition(track, tracks, endTime);
            const k = lastKeyValue(track, 'position');
            if (zone && carriedEnd) zone.position = clone(carriedEnd);
            else if (zone && k && Array.isArray(k.position)) zone.position = clone(k.position);
            const v = lastKeyValue(track, 'visual');
            if (zone && v && v.value) {
                const val = v.value;
                if (val.radius !== undefined) zone.radius = val.radius;
                if (val.color !== undefined) zone.color = val.color;
                if (val.opacity !== undefined) zone.opacity = val.opacity;
                if (val.thickness !== undefined) zone.thickness = val.thickness;
                if (val.closed !== undefined) zone.closed = val.closed;
                if (Array.isArray(val.points) && val.points.length) zone.points = clone(val.points);
                if (Array.isArray(val.pointRefs)) zone.pointRefs = clone(val.pointRefs);
            }
        }
    }
    return state;
}

/**
 * Copy the parent's END values onto a child branch as its read-only first key.
 *
 * Every track that exists in the parent gets a key at INHERITED_KEY_TIME in the
 * child, carrying the parent's final value for that key type. Position, attach,
 * graphic and zone-visual keys are states, so they carry over; action keys are
 * one-shot animation triggers and are not inherited.
 */
export function seedBranchFromParent(parent, child) {
    if (!parent || !child) return child;
    const tracks = [];
    for (const pTrack of parent.keyframes || []) {
        const track = { id: pTrack.id, type: pTrack.type };
        if (pTrack.side) track.side = pTrack.side;
        if (pTrack.index !== undefined && pTrack.index !== null) track.index = pTrack.index;
        if (pTrack.ballId) track.ballId = pTrack.ballId;
        if (pTrack.coneId) track.coneId = pTrack.coneId;
        if (pTrack.targetId) track.targetId = pTrack.targetId;
        if (pTrack.zoneId) track.zoneId = pTrack.zoneId;

        // A carried ball's own last key is not where it was (see
        // attachedEndPosition) — the inherited instant has to hold the item's
        // EFFECTIVE position, or the child branch opens with it back at a stale
        // key it has not been near since before the attach.
        const carriedEnd = attachedEndPosition(pTrack, parent.keyframes || [], branchEndTime(parent));
        const pos = lastKeyValue(pTrack, 'position');
        const startPos = carriedEnd || (pos && Array.isArray(pos.position) ? pos.position : null);
        if (startPos) {
            track.position = [{ time: INHERITED_KEY_TIME, position: clone(startPos), inherited: true }];
        }
        const att = lastKeyValue(pTrack, 'attach');
        // A mark includes its bearing and gap, not just the opponent reference.
        // Dropping those settings moves the marker to the default spot at the seam.
        if (att) track.attach = [{ ...clone(att), time: INHERITED_KEY_TIME, inherited: true }];
        const gra = lastKeyValue(pTrack, 'graphic');
        if (gra) track.graphic = [{ time: INHERITED_KEY_TIME, value: gra.value, inherited: true }];
        const vis = lastKeyValue(pTrack, 'visual');
        if (vis) track.visual = [{ time: INHERITED_KEY_TIME, value: clone(vis.value), inherited: true }];

        if (track.position || track.attach || track.graphic || track.visual) tracks.push(track);
    }
    child.keyframes = tracks;
    if (child.phases && child.phases[0]) {
        child.phases[0].start = branchEndState(parent);
    }
    return child;
}

/**
 * Re-run inheritance for a child branch against its current parent (used when
 * the parent's timeline changed and the child has to pick up the new end
 * state). Existing non-inherited keys in the child are preserved.
 *
 * Returns the SAME tree object when the child was already in step, so a sync
 * only ever rewrites branches that were genuinely out of step — and so saving an
 * unchanged scenario stays a no-op.
 */
export function refreshInheritance(tree, childId) {
    const parent = findParent(tree, childId);
    if (!parent) return tree;   // root: nothing inherited
    const child = findBranch(tree, childId);
    if (!child) return tree;

    const fresh = seedBranchFromParent(parent, { ...child, keyframes: [], phases: clone(child.phases) });
    // keep everything the child already had beyond the inherited t=0 keys
    const merged = fresh.keyframes.map((track) => {
        const existing = (child.keyframes || []).find(t => t.id === track.id);
        if (!existing) return track;
        const out = { ...existing, ...track };
        for (const kind of ['position', 'attach', 'graphic', 'visual']) {
            const inheritedKeys = track[kind] || [];
            if (inheritedKeys.length || existing[kind]) {
                let own = (existing[kind] || []).filter(k => !isInheritedKey(k));
                if (inheritedKeys.length) {
                    // The inherited instant belongs to the inherited key — it IS
                    // the parent's end state. A scenario saved before inheritance
                    // existed carries a stale EDITABLE key on that same instant,
                    // and keeping both left two keys stacked at t=0 whose values
                    // disagreed: the seam. Drop the stale one.
                    own = own.filter(k => k.time !== INHERITED_KEY_TIME);
                }
                out[kind] = [...inheritedKeys, ...own].sort((a, b) => a.time - b.time);
            }
        }
        return out;
    });
    const extra = (child.keyframes || []).filter(t => !fresh.keyframes.some(f => f.id === t.id));
    // fresh.phases carries the parent's end state as this branch's start state
    // (phases[0].start), which is what the branch is based on — so it has to
    // travel with the keys, not lag behind them.
    const updated = { ...child, keyframes: [...merged, ...extra], phases: fresh.phases };

    if (JSON.stringify(updated) === JSON.stringify(child)) return tree;   // already in step
    return updateBranch(tree, childId, () => updated);
}

/** Was this key inherited from the parent branch (i.e. read-only)? */
export function isInheritedKey(key) {
    return !!(key && key.inherited);
}

/**
 * Re-derive inheritance for a branch's whole sub-tree, so a child's read-only
 * first key always matches the END of its parent (and a grandchild matches the
 * end of the child).
 *
 * WHY THIS EXISTS: a child's first key is a copy of its parent's end state, made
 * when the child was created. Every later edit to the parent — moving it to a
 * different time, moving a player, deleting its last key — changes that end
 * state, and nothing else would ever update the copy. That is exactly how a
 * parent's last key and a child's first key drift apart. So this runs whenever a
 * branch's timeline can have changed.
 *
 * Order matters: parents before children (breadth-first), because an untouched
 * child ends wherever its parent ended, so its own children have to inherit from
 * the freshly synced value, not the stale one.
 *
 * @param {object} tree
 * @param {string|null} fromBranchId  sync this branch's sub-tree only; whole tree when null
 */
export function syncTreeInheritance(tree, fromBranchId = null) {
    if (!tree) return tree;
    tree = reconcileTreeItems(tree);
    const start = fromBranchId ? findBranch(tree, fromBranchId) : tree;
    if (!start) return tree;

    let next = tree;
    const queue = [start.id];
    const seen = new Set();
    while (queue.length) {
        const id = queue.shift();
        if (seen.has(id)) continue;   // guards against a cycle in corrupted data
        seen.add(id);
        next = refreshInheritance(next, id);   // no-op for the root
        const branch = findBranch(next, id);
        for (const child of (branch && branch.children) || []) queue.push(child.id);
    }
    return next;
}

/**
 * The time before which keys cannot be edited in this branch: 0 for every
 * non-root branch (its inherited first key), null for the root.
 */
export function lockedStartTime(tree, branchId) {
    if (!tree || !branchId) return null;
    return isRootBranch(tree, branchId) ? null : INHERITED_KEY_TIME;
}

// ─────────────────────────────────────────────────────────────
// COPY / PASTE — reuse one branch's timeline in another branch
// ─────────────────────────────────────────────────────────────

/**
 * Everything that makes up a branch's TIMELINE: its keys, its per-phase start
 * states and its length. Deliberately NOT its identity — name, priority, id and
 * sub-branches stay with the branch itself, so a paste never renames or
 * re-parents anything.
 *
 * The read-only first key a non-root branch inherits from its parent is left
 * out. It describes where THIS branch starts; the branch receiving the paste
 * may hang from a different parent, so its own inherited key is re-derived from
 * its own parent by pasteBranchTimeline instead of being carried over.
 *
 * Tracks that held nothing but that inherited key carry no information and are
 * dropped, so a paste cannot litter the target with empty tracks.
 */
export function branchTimelineSnapshot(branch) {
    if (!branch) return null;
    const keyframes = clone(branch.keyframes || []).map((track) => {
        const out = { ...track };
        for (const kind of ['position', 'attach', 'graphic', 'visual']) {
            if (Array.isArray(track[kind])) out[kind] = track[kind].filter(k => !isInheritedKey(k));
        }
        return out;
    }).filter(track => ['position', 'attach', 'graphic', 'visual', 'action']
        .some(kind => (track[kind] || []).length));
    return {
        phases: clone(branch.phases || []),
        keyframes,
        timelineDuration: branch.timelineDuration ?? DEFAULT_BRANCH_DURATION,
    };
}

/**
 * Paste a timeline snapshot INTO a branch: its keys, phase start states and
 * length are replaced, while its id, name, priority and sub-branches survive
 * untouched.
 *
 * The inherited t=0 keys are then re-derived down the sub-tree, because pasted
 * keys change where this branch ENDS — and every child's first key is a copy of
 * its parent's end state (see syncTreeInheritance).
 */
export function pasteBranchTimeline(tree, targetId, snapshot) {
    if (!tree || !snapshot) return tree;
    if (!findBranch(tree, targetId)) return tree;
    const updated = updateBranch(tree, targetId, (branch) => ({
        ...branch,
        phases: clone(snapshot.phases || []),
        keyframes: clone(snapshot.keyframes || []),
        timelineDuration: snapshot.timelineDuration ?? DEFAULT_BRANCH_DURATION,
    }));
    return syncTreeInheritance(updated, targetId);
}

// ─────────────────────────────────────────────────────────────
// MIGRATION — any scenario (V2 or V3) becomes a V3 tree
// ─────────────────────────────────────────────────────────────

/**
 * One phase, migrated for V3.
 *
 * Both the V2 path and the V3 path go through here, because the pitch states
 * inside a phase are where the old model lives: a legacy single `ball` becomes
 * the `balls` array (and an old follow-a-player arc becomes `attached`), old
 * speed/offset arc fields become offsetStart/offsetEnd, zones gain their newer
 * fields. The legacy `transition` pose is migrated too and kept — V3 does not
 * animate from it, but an old scenario's work is not ours to delete.
 */
function migratePhase(p) {
    return {
        ...p,
        duration: p.duration ?? 3,
        start: migratePitchState(p.start),
        ...(p.transition !== undefined && p.transition !== null
            ? { transition: migratePitchState(p.transition) }
            : {}),
    };
}

/** Wrap a plain V2 scenario into a single-root V3 tree (no data lost). */
export function treeFromV2Scenario(scenario) {
    if (!scenario) return createBranch({ id: 'root', name: 'Root', order: 1 });
    return {
        id: 'root',
        name: 'Root',
        order: 1,
        phases: (scenario.phases && scenario.phases.length
            ? scenario.phases
            : [createEmptyPhase(1)]).map(migratePhase),
        keyframes: clone(scenario.keyframes || []),
        timelineDuration: scenario.timelineDuration ?? DEFAULT_BRANCH_DURATION,
        children: [],
    };
}

/**
 * Normalise anything the API hands us into a V3 scenario with a tree.
 *   • already a V3 tree → migrated in place (phases of every branch migrated)
 *   • a V2 scenario     → wrapped as a lone root branch
 */
export function migrateScenarioV3(scenario) {
    scenario = withoutScenarioTeams(scenario);
    if (!scenario) return scenario;

    const migrateBranch = (branch) => ({
        ...branch,
        order: branch.order ?? 1,
        phases: (branch.phases || [createEmptyPhase(1)]).map(migratePhase),
        keyframes: branch.keyframes || [],
        timelineDuration: branch.timelineDuration ?? DEFAULT_BRANCH_DURATION,
        children: (branch.children || []).map(migrateBranch),
    });

    if (scenario.tree && scenario.tree.id) {
        return {
            ...scenario,
            schemaVersion: V3_SCENE_VERSION,
            sceneVersion: V3_SCENE_VERSION,
            tree: migrateBranch(scenario.tree),
        };
    }

    if (!scenario.phases) return scenario;
    return {
        ...scenario,
        schemaVersion: V3_SCENE_VERSION,
        sceneVersion: V3_SCENE_VERSION,
        tree: treeFromV2Scenario(scenario),
    };
}

/**
 * The payload shape written to the backend: the tree plus the root branch's
 * timeline mirrored into `phases`/`keyframes`/`timelineDuration`, so a V2
 * frontend can still open the scenario (it will see the root timeline only).
 */
export function scenarioForSave(scenario) {
    // Last line of defence: whatever path produced this tree, the payload is
    // synced so a saved scenario can never carry a parent/child key mismatch.
    const tree = withoutScenarioTeams(syncTreeInheritance(scenario.tree));
    return {
        name: scenario.name,
        sceneVersion: V3_SCENE_VERSION,
        tree,
        phases: tree.phases,
        keyframes: tree.keyframes || [],
        timelineDuration: tree.timelineDuration ?? DEFAULT_BRANCH_DURATION,
    };
}

/** Count every branch in the tree (for UI summaries). */
export function countBranches(tree) {
    if (!tree) return 0;
    return 1 + (tree.children || []).reduce((n, c) => n + countBranches(c), 0);
}

/**
 * Flatten the tree into a list of { branch, depth, parentId, path } in display
 * order (top-priority first) — handy for tests and summaries.
 */
export function flattenTree(tree) {
    const out = [];
    const walk = (branch, depth, parentId, path) => {
        out.push({ branch, depth, parentId, path });
        for (const child of sortedChildren(branch)) {
            walk(child, depth + 1, branch.id, [...path, branch.id]);
        }
    };
    if (tree) walk(tree, 0, null, []);
    return out;
}

/** Advance a prepared walk without dropping elapsed time at branch boundaries. */
export function advanceMasterPlayback(branches, index, time, delta, stopAtNextBranch = false) {
    let remaining = time + Math.max(0, delta);
    let nextIndex = index;
    while (nextIndex < branches.length) {
        const duration = Math.max(0, branches[nextIndex].timelineDuration ?? DEFAULT_BRANCH_DURATION);
        if (remaining < duration) return { index: nextIndex, time: remaining, ended: false };
        if (nextIndex === branches.length - 1) return { index: nextIndex, time: duration, ended: true };
        if (stopAtNextBranch) return { index: nextIndex + 1, time: 0, ended: true };
        remaining -= duration;
        nextIndex += 1;
    }
    return { index, time, ended: true };
}
